// =============================================================================
// Design Tokens - Scribia (styled-components theme)
// Mirrors _variables.scss for use in styled-components
// Typography: EB Garamond (headings) + Lato (body)
// =============================================================================

const theme = {
  colors: {
    primary50: '#f0f4fa',
    primary100: '#dce4f2',
    primary200: '#b9c9e5',
    primary300: '#8ea8d3',
    primary400: '#4e6fa3',
    primary: '#1b2a4a',
    primaryLight: '#2d4470',
    primaryDark: '#111c33',
    primary800: '#0e1629',
    primary900: '#0a1124',

    secondary: '#475569',
    secondaryLight: '#64748b',
    secondaryDark: '#334155',

    accent50: '#fdf8ed',
    accent100: '#f9edd2',
    accent200: '#f2daa5',
    accent300: '#e8c26f',
    accent: '#d4a843',
    accentLight: '#e0be6a',
    accentDark: '#b8922e',
    accent700: '#96760f',
    accent800: '#7a600d',
    accent900: '#5e490a',

    success: '#16a34a',
    successLight: '#22c55e',
    warning: '#d97706',
    warningLight: '#f59e0b',
    danger: '#dc2626',
    dangerLight: '#ef4444',
    info: '#2563eb',
    infoLight: '#3b82f6',

    gray50: '#fafaf9',
    gray100: '#f5f5f4',
    gray200: '#e7e5e4',
    gray300: '#d6d3d1',
    gray400: '#a8a29e',
    gray500: '#78716c',
    gray600: '#57534e',
    gray700: '#44403c',
    gray800: '#292524',
    gray900: '#1c1917',

    bgBody: '#fafaf9',
    bgSurface: '#ffffff',
    bgSurfaceRaised: '#ffffff',
    bgSurfaceElevated: '#ffffff',
    bgSidebar: '#1b2a4a',
    bgHeader: '#ffffff',
    bgMuted: '#f5f5f4',

    textPrimary: '#1c1917',
    textSecondary: '#57534e',
    textMuted: '#78716c',
    textInverse: '#ffffff',
    textLink: '#2563eb',
  },

  fonts: {
    heading: "'EB Garamond', Georgia, 'Times New Roman', serif",
    sans: "'Lato', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace",
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    md: '1.125rem',
    lg: '1.25rem',
    xl: '1.5rem',
    '2xl': '1.875rem',
    '3xl': '2.25rem',
  },

  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },

  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
  },

  radii: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.625rem',
    xl: '1rem',
    '2xl': '1.25rem',
    full: '9999px',
  },

  shadows: {
    xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    sm: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    elevated: '0 8px 24px -4px rgb(0 0 0 / 0.12), 0 2px 8px -2px rgb(0 0 0 / 0.08)',
  },

  layout: {
    headerHeight: '64px',
    contentMaxWidth: '1200px',
  },

  transitions: {
    fast: '150ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },

  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
} as const;

export type Theme = typeof theme;
export default theme;
